/**
 * LocalStorage 工具函数
 * 用于点赞、关注池等本地状态管理
 */

// 存储 Key
const STORAGE_KEYS = {
  LIKES: 'web3_events_likes',
  WATCHLIST: 'web3_watchlist_accounts',
}

// ========================================
// 点赞功能
// ========================================

export interface LikeState {
  [eventId: string]: boolean
}

/**
 * 获取所有点赞状态
 */
export function getLikes(): LikeState {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.LIKES)
    return data ? JSON.parse(data) : {}
  } catch {
    return {}
  }
}

/**
 * 检查是否已点赞
 */
export function isLiked(eventId: string): boolean {
  const likes = getLikes()
  return !!likes[eventId]
}

/**
 * 切换点赞状态
 */
export function toggleLike(eventId: string): boolean {
  const likes = getLikes()
  const currentState = !!likes[eventId]
  likes[eventId] = !currentState
  try {
    localStorage.setItem(STORAGE_KEYS.LIKES, JSON.stringify(likes))
  } catch (e) {
    console.error('Failed to save likes:', e)
  }
  return !currentState // 返回新状态
}

/**
 * 设置点赞状态
 */
export function setLike(eventId: string, liked: boolean): void {
  const likes = getLikes()
  likes[eventId] = liked
  try {
    localStorage.setItem(STORAGE_KEYS.LIKES, JSON.stringify(likes))
  } catch (e) {
    console.error('Failed to save likes:', e)
  }
}

// ========================================
// 关注池功能
// ========================================

export interface WatchlistAccount {
  screen_name: string
  layer?: string
  added_at: number // timestamp
  source_event_id?: string // 来源事件 ID
}

export interface WatchlistState {
  accounts: WatchlistAccount[]
}

/**
 * 获取关注池
 */
export function getWatchlist(): WatchlistState {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.WATCHLIST)
    if (data) {
      const parsed = JSON.parse(data)
      return { accounts: parsed.accounts || [] }
    }
  } catch {
    // ignore
  }
  return { accounts: [] }
}

/**
 * 检查账号是否在关注池中
 */
export function isInWatchlist(screenName: string): boolean {
  const watchlist = getWatchlist()
  return watchlist.accounts.some(a => a.screen_name.toLowerCase() === screenName.toLowerCase())
}

/**
 * 添加账号到关注池
 */
export function addToWatchlist(account: Omit<WatchlistAccount, 'added_at'>): boolean {
  const watchlist = getWatchlist()
  
  // 检查是否已存在
  if (isInWatchlist(account.screen_name)) {
    return false
  }
  
  watchlist.accounts.push({
    ...account,
    added_at: Date.now(),
  })
  
  try {
    localStorage.setItem(STORAGE_KEYS.WATCHLIST, JSON.stringify(watchlist))
    return true
  } catch (e) {
    console.error('Failed to save watchlist:', e)
    return false
  }
}

/**
 * 从关注池移除账号
 */
export function removeFromWatchlist(screenName: string): boolean {
  const watchlist = getWatchlist()
  const index = watchlist.accounts.findIndex(
    a => a.screen_name.toLowerCase() === screenName.toLowerCase()
  )
  
  if (index === -1) {
    return false
  }
  
  watchlist.accounts.splice(index, 1)
  
  try {
    localStorage.setItem(STORAGE_KEYS.WATCHLIST, JSON.stringify(watchlist))
    return true
  } catch (e) {
    console.error('Failed to save watchlist:', e)
    return false
  }
}

/**
 * 批量添加账号到关注池
 */
export function addManyToWatchlist(
  accounts: Array<{ screen_name: string; layer?: string }>,
  sourceEventId?: string
): number {
  let addedCount = 0
  for (const account of accounts) {
    if (addToWatchlist({ ...account, source_event_id: sourceEventId })) {
      addedCount++
    }
  }
  return addedCount
}

/**
 * 清空关注池
 */
export function clearWatchlist(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.WATCHLIST)
  } catch {
    // ignore
  }
}
